import { colors } from "@apps/viewer/src/ts/config";
import React from "react";
import ReactDOM from "react-dom";

import { FadeSlide } from "@apps/viewer/src/ts/components/utils/Tooltip/FadeSlide";

type TooltipTargetRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type TooltipProps = {
  /**
   * Specifies if the tooltip should currently be displayed or not
   */
  active: boolean;

  /**
   * If enabled, the tooltip will used a fixed positioning rather than the
   * normal absolute positioning.
   */
  fixed?: boolean;

  /**
   * Contains a reference to the target that the tooltip should point to
   */
  targetRef: React.RefObject<HTMLElement | null>;

  /**
   * Specifies the width of the tooltip in order to ensure that it'll remain
   * within viewport bounds
   */
  tooltipWidth: number;

  children: React.ReactNode;
};

type TooltipState = {
  /**
   * Specifies how far the tooltip should be offset to the left in order
   * to ensure that it remains within viewport
   */
  offsetLeft: number;

  /**
   * Specifies the position of the target that the Tooltip should point to.
   * Will be determined internally based on targetRef
   */
  targetPos?: TooltipTargetRect;
};

/**
 * Specifies the minimum amount of space that should be available to the
 * left of the tooltip.
 */
const MIN_MARGIN = 15;

/**
 * Component responsible for rendering a tooltip which should be
 * positioned within a wrapper next to the content that it is related to.
 *
 * Content to be rendered within the tooltip should be passed via props.children
 */
export class Tooltip extends React.PureComponent<TooltipProps, TooltipState> {
  /**
   * Setup initial state
   */
  public state: TooltipState = {
    offsetLeft: 0,
  };

  /**
   * Specifies whether or not we need to verify that the tooltip is within
   * the viewport
   */
  private needsUpdate: boolean = true;

  /**
   * Contains a reference to the tooltip element
   */
  private readonly tooltipRef: React.RefObject<HTMLDivElement | null> =
    React.createRef();

  /**
   * Ensure tooltip is within viewport on mount
   */
  public componentDidMount(): void {
    this.determinePosition();
  }

  /**
   * Ensure tooltip is within viewport every time that it is required
   */
  public componentDidUpdate(): void {
    if (this.props.active && this.needsUpdate) {
      this.determinePosition();
    } else if (!this.props.active) {
      this.needsUpdate = true;
    }
  }

  /**
   * Main render method
   */
  public render(): React.ReactNode {
    const { targetPos } = this.state;

    // Should only be visible while active and we currently aren't in need
    // of updating position
    const visible = this.props.active && !this.needsUpdate;

    return ReactDOM.createPortal(
      <FadeSlide in={visible}>
        {(ref) => (
          <div
            ref={ref}
            className={`tooltip ${this.props.fixed ? "tooltip--fixed" : ""}`}
          >
            <div
              className="tooltip__container"
              ref={this.tooltipRef}
              style={{
                transform: `translate(calc(-50% + ${this.state.offsetLeft}px), -100%)`,
              }}
            >
              <i
                className="tooltip__arrow"
                style={{
                  marginLeft: `${this.state.offsetLeft * -1}px`,
                }}
              />

              {this.props.children}
            </div>

            <style jsx>{`
              .tooltip {
                /**
                             * Position tooltip on top of item related to the
                             * tooltip.
                             */
                position: absolute;
                top: ${targetPos ? targetPos.top : 0}px;
                left: ${targetPos ? targetPos.left : 0}px;

                /** Expand to fill dimensions of related item */
                width: ${targetPos ? targetPos.width : 0}px;
                height: ${targetPos ? targetPos.height : 0}px;

                /** Ensure tooltip is shown on top of other content */
                z-index: 101;

                /**
                             * Disable pointer-events on tooltip since
                             * it'll overlap the content it is pointing to
                             */
                pointer-events: none;

                &.tooltip--fixed {
                  /** Use fixed positioning instead */
                  position: fixed;
                }
              }

              .tooltip__container {
                /** Position preview 5px above content it is rendered in relation to */
                position: absolute;
                top: -15px;
                left: 50%;
                transform: translate(-50%, -100%);

                /** Reenable pointer-events on the tooltip itself */
                pointer-events: auto;

                /** Render the preview block */
                background-color: ${colors.white};
                border-bottom: 4px solid ${colors.darkGrey};

                /** Render a box-shadow to separate preview from other content */
                box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);

                /** Render the arrow displayed below the tooltip */
                & .tooltip__arrow {
                  /** Setup positioning */
                  position: absolute;
                  bottom: -3px; /** Position below border */
                  left: 50%;
                  transform: translate(-50%, 100%);

                  /** Render the actual arrow */
                  border-left: 9px solid transparent;
                  border-right: 9px solid transparent;
                  border-top: 9px solid ${colors.darkGrey};
                }
              }
            `}</style>
            <style jsx>{`
              .tooltip::before {
                transform: translate(
                  calc(-50% - ${this.state.offsetLeft}px),
                  100%
                );
              }
            `}</style>
          </div>
        )}
      </FadeSlide>,
      document.body
    );
  }

  /**
   * Helper that'll determine the position of the tooltip based on the provided
   * targetRef
   */
  protected determinePosition = () => {
    if (!this.props.targetRef.current) {
      return;
    }

    const targetRect = this.props.targetRef.current.getBoundingClientRect();

    const targetPos: TooltipTargetRect = {
      top: targetRect.top,
      left: targetRect.left,
      width: targetRect.width,
      height: targetRect.height,
    };

    // If we're not using fixed position, then we're going to have to adjust
    // position based on current scroll level
    if (!this.props.fixed) {
      targetPos.left += window.pageXOffset;
      targetPos.top += window.pageYOffset;
    }

    // Now that we've ensured that the tooltip is within the viewport, then
    // we don't need to do so anymore
    this.needsUpdate = false;
    this.setState({
      targetPos,
      offsetLeft: this.ensureTooltipIsWithinViewport(targetPos),
    });
  };

  /**
   * Helper that ensures the tooltip stays within the viewport by calculating
   * how much the tooltip needs to be offset on the x-axis in order to achieve
   * this.
   */
  protected ensureTooltipIsWithinViewport = (
    targetRect: TooltipTargetRect
  ): number => {
    const left = targetRect.left - this.props.tooltipWidth / 2;
    const right =
      targetRect.left + targetRect.width + this.props.tooltipWidth / 2;

    const isOverflowingLeft = left < MIN_MARGIN;
    const isOverflowingRight = right > window.innerWidth - MIN_MARGIN;

    // Determine how much the tooltip overflows the viewport on the x-axis
    // while taking previous offsetLeft into accounts
    return isOverflowingLeft
      ? -left + MIN_MARGIN
      : isOverflowingRight
      ? -(right - window.innerWidth + MIN_MARGIN)
      : 0;
  };
}
